import { useNavigate } from "react-router-dom";
import { ZodObject, ZodRawShape, z, ZodEffects } from "zod";
import { useAppDispatch, useAppSelector } from "./useTypedSelector";
import React, { useEffect, useState } from "react";
import {
  clear,
  crudCreate,
  crudRead,
  crudUpdate,
  crudDelete,
  initMutate,
} from "../root/general/infrastructure/slices/crudMutateSlice/crudMutateSlice";
import { useForm } from "@tanstack/react-form";
import { zodValidator } from "@tanstack/zod-form-adapter";
import {
  CrudForm,
  generateInput,
} from "../root/general/modules/commons/crud/crudForm";
import { Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { ContentHeader } from "../root/general/modules/commons/content/contentHeader";
import swal from "sweetalert";
import { getEntityLog } from "../root/logs/infrastructure/slices/logSlice/logSlice";
import { LogModel } from "../root/logs/domain/models/logModels";
import { datePickerOptions } from "../root/general/modules/commons/crud/inputs/crudInputModel";
import { EntitySelectFieldModel } from "../root/general/fields/modules/commons/entitySelectField/entitySelectFieldModel";
import { useAuthorization } from "./useAuthorization";
import { Content } from "../root/general/modules/commons/content/content";

export interface CrudFormModel {
  schema: ZodObject<ZodRawShape>;
  prefix: string;
  createMutation: string;
  updateMutation: string;
  deleteMutation?: string;
  id?: number | undefined;
  manageFields?: string[] | undefined;
  defaultValues?: z.infer<ZodObject<ZodRawShape>>;
  disabledFields?: (string | undefined)[] | undefined;
  hiddenFields?: (string | undefined)[] | undefined;
  editUrl?: string;
  deleteUrl?: string;
  queryExclude?: string[];
  mutationExclude?: string[];
  formTemplate?: (() => JSX.Element) | undefined;
  onAfterSubmit?: (result: object) => void;
  onBeforeSubmit?: (result: object) => object;
  inputGroupFields?: { [key: string]: string };
  ignoreFields?: string[] | undefined;
  selectOptions?: {
    [key: string]: {
      label: string;
      value: string | number;
      isDisabled: boolean;
    }[];
  };
  datePickerOptions?: { [key: string]: datePickerOptions };
  entitySelectOptions?: { [key: string]: EntitySelectFieldModel };
  validationSchema?:
    | ZodObject<ZodRawShape>
    | ZodEffects<ZodObject<ZodRawShape>>;
  showSubmitButton?: boolean;
  showDeleteButton?: boolean;
  submitButtonLabel?: string;
}

export default function useCrudForm({
  schema,
  prefix,
  createMutation,
  updateMutation,
  deleteMutation,
  id,
  formTemplate,
  manageFields,
  defaultValues,
  onAfterSubmit,
  onBeforeSubmit,
  editUrl,
  deleteUrl,
  queryExclude,
  mutationExclude,
  disabledFields,
  hiddenFields,
  inputGroupFields,
  ignoreFields,
  validationSchema,
  selectOptions,
  datePickerOptions,
  entitySelectOptions,
  showSubmitButton = true,
  showDeleteButton = true,
  submitButtonLabel,
}: CrudFormModel) {
  const { t } = useTranslation("common");
  const { hasRole } = useAuthorization();

  const navigate = useNavigate();

  const dispatch = useAppDispatch();

  const model = useAppSelector((state) => state.crudMutate).model as z.infer<
    typeof schema
  >;

  useEffect(() => {
    dispatch(initMutate({ schema, prefix, queryExclude, mutationExclude }));

    return () => {
      dispatch(clear());
    };
  }, []);

  useEffect(() => {
    if (id !== undefined) {
      void dispatch(crudRead({ id }));
    }
  }, [id]);

  const defaultFormValues =
    Object.keys(model).length > 0 ? model : ({} as z.infer<typeof schema>);

  if (Object.keys(model).length == 0) {
    for (const key of Object.keys(schema.shape)) {
      if (
        schema.shape[key] instanceof z.ZodDefault &&
        schema.shape[key]._def.defaultValue !== undefined
      ) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        defaultFormValues[key] = schema.shape[key]._def.defaultValue();
      }
    }
  }

  const form = useForm({
    defaultValues: {
      ...defaultFormValues,
      ...defaultValues,
    },
    onSubmit: (values) => {
      let data = values.value;

      if (onBeforeSubmit) {
        data = onBeforeSubmit(data);
      }

      if (data.validationError) {
        return;
      }

      ignoreFields?.forEach((field) => {
        delete data[field];
      });

      if (id !== undefined) {
        void dispatch(
          crudUpdate({ id: id, data: data, mutation: updateMutation }),
        ).then((result) => {
          if (crudUpdate.fulfilled.match(result) && onAfterSubmit) {
            onAfterSubmit(result.payload);
            return;
          }
        });
      } else {
        void dispatch(
          crudCreate({ data: data, mutation: createMutation }),
        ).then((result) => {
          if (crudCreate.fulfilled.match(result)) {
            if (onAfterSubmit) {
              onAfterSubmit(result.payload);
              return;
            }
            const { id } = result.payload as { id: number };

            if (editUrl) {
              navigate(editUrl.replace(":id", id.toString()));
              return;
            }

            navigate(`/${prefix}/edit/${id}`);
          }
        });
      }
    },
    validatorAdapter: zodValidator(),
    validators: {
      onSubmit: validationSchema,
    },
  });

  const deleteEntity = (deleteId: number, mutation: string) => {
    void swal({
      title: t(`crud.${prefix}.delete.confirm`),
      buttons: {
        cancel: {
          visible: true,
          className: "btn btn-light btn-sm",
        },
        confirm: {
          visible: true,
          className: "btn btn-primary btn-sm",
        },
      },
    }).then((willDelete) => {
      if (willDelete) {
        void dispatch(crudDelete({ id: deleteId, mutation: mutation })).then(
          (result) => {
            if (crudDelete.fulfilled.match(result)) {
              if (deleteUrl) {
                navigate(deleteUrl);
              } else {
                navigate(`/${prefix}`);
              }
            }
          },
        );
      }
    });
  };

  const submitButton = () => (
    <Button
      onClick={() => {
        void form.handleSubmit();
      }}
    >
      {submitButtonLabel ?? t(`submit`)}
    </Button>
  );

  const deleteButton = (deleteId: number, mutation: string) => (
    <Button
      className={"btn-light"}
      onClick={() => {
        deleteEntity(deleteId, mutation);
      }}
    >
      {t(`delete`)}
    </Button>
  );

  const statusField = () => {
    return (
      schema.shape.status !== undefined &&
      generateInput(
        schema.shape.status,
        99,
        form,
        "status",
        t(`crud.${prefix}.status`),
        disabledFields?.includes("status") || false,
        t,
      )
    );
  };

  const activeField = () => {
    return (
      schema.shape.active !== undefined &&
      generateInput(
        schema.shape.active,
        99,
        form,
        "active",
        t(`crud.${prefix}.active`),
        disabledFields?.includes("active") || false,
        t,
      )
    );
  };

  const buildLogs = (prefix: string, id: number) => {
    const entity = (prefix.charAt(0).toUpperCase() + prefix.slice(1)).slice(
      0,
      -1,
    );
    const [loadingLogs, setLoadingLogs] = useState(true);
    const [entityLogs, setEntityLogs] = useState<LogModel[]>([]);
    const [showLogs, setShowLogs] = useState(false); // State to toggle visibility

    type LogProperties = {
      old?: Record<string, unknown>;
      attributes?: Record<string, unknown>;
      [key: string]: unknown;
    };

    const cleanObject = (obj: unknown): unknown => {
      if (Array.isArray(obj)) {
        return obj
          .map(cleanObject)
          .filter((value) => value !== null && value !== undefined);
      } else if (obj !== null && typeof obj === "object") {
        const typedObj = obj as LogProperties;

        if (typedObj.old && typedObj.attributes) {
          typedObj.old = Object.fromEntries(
            Object.entries(typedObj.old).filter(([key, value]) => {
              const attributesValue = typedObj.attributes?.[key];
              return (
                value !== undefined ||
                (attributesValue !== null && attributesValue !== undefined)
              );
            }),
          );
        }

        return Object.fromEntries(
          Object.entries(typedObj)
            .map(([key, value]) => [key, cleanObject(value)])
            .filter(
              ([key, value]) =>
                value !== null &&
                value !== undefined &&
                key !== "tireset_sticker_config",
            ),
        );
      }
      return obj;
    };

    useEffect(() => {
      const fetchLogs = async () => {
        try {
          const result = await dispatch(getEntityLog({ entity, id })).unwrap();

          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          const cleanedLogs = result.data.map((log: LogModel) => ({
            ...log,
            properties: cleanObject(log.properties),
          }));

          setEntityLogs(cleanedLogs as LogModel[]);
        } catch (error) {
          console.error("Error fetching logs:", error);
        }
      };

      if (!entityLogs.length) {
        void fetchLogs().then(() => setLoadingLogs(false));
      }
    }, []);

    const handleToggleLogs = () => {
      document.body.style.overflow = showLogs ? "auto" : "hidden";
      setShowLogs((prev) => !prev);
    };

    const [openDropdowns, setOpenDropdowns] = useState<Record<number, boolean>>(
      {},
    );

    const toggleDropdown = (index: number) => {
      setOpenDropdowns((prevState) => ({
        ...prevState,
        [index]: !prevState[index],
      }));
    };

    return (
      <Content className={"mt-4"}>
        <div className={"d-flex flex-row"}>
          <ContentHeader title={t("crud.logs.header.title")} />
          <Button
            onClick={handleToggleLogs}
            className="btn btn-primary"
            style={{ marginLeft: "auto" }}
          >
            {showLogs ? t("crud.logs.hideLogs") : t("crud.logs.showLogs")}
          </Button>
        </div>
        {showLogs && (
          <div className="mt-3 popup-bg">
            <div className={"d-flex flex-column popup-logs"}>
              {entityLogs.length === 0 ? (
                loadingLogs ? (
                  <>
                    <div
                      className="alert alert-info"
                      style={{ marginBottom: 10 }}
                    >
                      Loading...
                    </div>
                    <Button
                      size={"sm"}
                      variant={"light"}
                      style={{ marginRight: "10px" }}
                      onClick={handleToggleLogs}
                    >
                      {t("Back")}
                    </Button>
                  </>
                ) : (
                  <>
                    <div
                      className="alert alert-info"
                      style={{ marginBottom: 10 }}
                    >
                      {t(`crud.logs.noLogs`)}
                    </div>
                    <Button
                      size={"sm"}
                      variant={"light"}
                      style={{ marginRight: "auto" }}
                      onClick={handleToggleLogs}
                    >
                      {t("Back")}
                    </Button>
                  </>
                )
              ) : (
                <div>
                  <ContentHeader title={t(`crud.logs.title`)} />
                  <div
                    className="table-responsive mt-3"
                    style={{ marginBottom: "-1rem" }}
                  >
                    <table className="table table-sm table-striped">
                      <thead>
                        <tr>
                          <th>{t(`crud.logs.causer`)}</th>
                          <th className={"text-center"}>
                            {t(`crud.logs.event`)}
                          </th>
                          <th className={"text-center"}>
                            {t(`crud.logs.changed`)}
                          </th>
                          <th className={"text-end"}>
                            {t(`crud.logs.created_at`)}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {entityLogs.map((log: LogModel, i) => {
                          const oldProperties = log.properties.old;
                          const newProperties = log.properties.attributes;

                          return (
                            <tr key={i}>
                              <td
                                style={{
                                  height: "33px",
                                }}
                              >
                                {log.causer.name ?? log.causer.username}
                              </td>

                              <td
                                style={{
                                  height: "33px",
                                }}
                                className={"text-center"}
                              >
                                {log.event}
                              </td>

                              <td
                                style={{
                                  height: "33px",
                                }}
                                className={"text-center"}
                              >
                                {!oldProperties && !newProperties ? (
                                  <div>
                                    <p>{log.description}</p>
                                  </div>
                                ) : newProperties &&
                                  Object.keys(newProperties).length === 1 ? (
                                  <div>
                                    {Object.keys(newProperties).map((key) => (
                                      <div
                                        key={key}
                                        style={{
                                          marginBottom: "-16px",
                                        }}
                                      >
                                        <p>
                                          <strong>
                                            {key.charAt(0).toUpperCase() +
                                              key.slice(1)}
                                            :
                                          </strong>{" "}
                                          {oldProperties ? (
                                            <>
                                              {JSON.stringify(
                                                oldProperties[key],
                                              )}{" "}
                                              {"->"}{" "}
                                              {JSON.stringify(
                                                newProperties[key],
                                              )}
                                            </>
                                          ) : (
                                            JSON.stringify(newProperties[key])
                                          )}
                                        </p>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <div className="d-flex flex-column justify-content-start">
                                    <div
                                      style={{
                                        cursor: "pointer",
                                        color: "blue",
                                      }}
                                      onClick={() => toggleDropdown(i)}
                                    >
                                      (
                                      {newProperties &&
                                        Object.keys(newProperties).length}
                                      ){" "}
                                      {openDropdowns[i]
                                        ? t("Hide Properties")
                                        : t("Show Properties")}{" "}
                                      <span>
                                        {openDropdowns[i] ? "↑" : "↓"}
                                      </span>
                                    </div>
                                    {openDropdowns[i] && (
                                      <div
                                        style={{
                                          marginTop: "10px",
                                        }}
                                      >
                                        <h6>Properties</h6>
                                        {Object.keys(newProperties).map(
                                          (key) => (
                                            <div
                                              key={key}
                                              style={{
                                                marginBottom: "-12px",
                                              }}
                                            >
                                              {oldProperties ? (
                                                <p>
                                                  <strong>{key}:</strong>{" "}
                                                  {JSON.stringify(
                                                    oldProperties[key],
                                                  )}{" "}
                                                  {"->"}{" "}
                                                  {JSON.stringify(
                                                    newProperties[key],
                                                  )}
                                                </p>
                                              ) : (
                                                <p>
                                                  <strong>{key}:</strong>{" "}
                                                  {JSON.stringify(
                                                    newProperties[key],
                                                  )}
                                                </p>
                                              )}
                                            </div>
                                          ),
                                        )}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </td>

                              <td
                                style={{
                                  height: "33px",
                                }}
                                className={"text-end"}
                              >
                                {log.created_at}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <Button
                    size={"sm"}
                    variant={"danger"}
                    style={{
                      marginRight: "10px",
                      marginTop: "10px",
                    }}
                    onClick={handleToggleLogs}
                  >
                    {t("Back")}
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </Content>
    );
  };

  return {
    form: (
      <CrudForm
        schema={schema}
        prefix={prefix}
        form={form}
        formTemplate={formTemplate}
        disabledFields={disabledFields}
        hiddenFields={hiddenFields}
        inputGroupFields={inputGroupFields}
        selectOptions={selectOptions}
        datePickerOptions={datePickerOptions}
        entitySelectOptions={entitySelectOptions}
      />
    ),
    formLogic: form,
    manage: (
      <>
        {statusField()}
        {activeField()}
        {manageFields?.map((field, i) => {
          const schemaField = schema.shape[field];
          return generateInput(
            schemaField,
            99 + i,
            form,
            field,
            t(`crud.${prefix}.${field}`),
            disabledFields?.includes(field) || false,
            t,
            inputGroupFields?.[field],
          );
        })}
        <div className={"d-flex mt-3 gap-3"}>
          {showSubmitButton && submitButton()}
          {id &&
            deleteMutation &&
            showDeleteButton &&
            deleteButton(id, deleteMutation)}
        </div>
      </>
    ),
    manageHeader: (
      <ContentHeader title={t("crud.manage.title")} className="mb-3" />
    ),
    submit: () => void form.handleSubmit(),
    formSubmit: (
      <>
        <div style={{ marginBottom: "-1rem", marginRight: 16 }}>
          {statusField()}
        </div>
        <div className={"d-flex justify-content-between"}>
          {submitButton()}
          {id && deleteMutation && deleteButton(id, deleteMutation)}
        </div>
      </>
    ),
    logs:
      hasRole("dbh-manager") || hasRole("super-admin") ? (
        buildLogs(prefix, id as number)
      ) : (
        <></>
      ),
  };
}
